/**
 * API — Diffusion (LOT 3 — iter92).
 * Encapsule les 2 endpoints backend pour rester découplé de l'UI et permettre
 * un mock/replay simple en test.
 */
import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const activateSupport = async (propertyId, support) => {
  const res = await axios.post(
    `${API}/properties/${propertyId}/diffusion/${support}`,
    { action: 'activate' },
    { headers: authHeaders() }
  );
  return res.data;
};

export const deactivateSupport = async (propertyId, support) => {
  const res = await axios.post(
    `${API}/properties/${propertyId}/diffusion/${support}`,
    { action: 'deactivate' },
    { headers: authHeaders() }
  );
  return res.data;
};

export const fetchDiffusionOverview = async (page = 1, pageSize = 50, opts = {}) => {
  const res = await axios.get(`${API}/diffusion/overview`, {
    headers: authHeaders(),
    // lot T1 — `q` / `support` / `status` sont appliqués côté serveur : la recherche porte
    // sur tout le portefeuille, plus seulement sur la page affichée.
    params: {
      page,
      page_size: pageSize,
      ...(opts.q ? { q: opts.q } : {}),
      ...(opts.support ? { support: opts.support } : {}),
      ...(opts.status && opts.status !== 'all' ? { status: opts.status } : {}),
    },
  });
  return res.data;
};

/**
 * Lot Y6 — état réel de l'annonce chez SeLoger.
 * Le dépôt répond 202 (demande acceptée) : seul `GET /classifieds/{id}/statuses` dit si
 * l'annonce est en ligne. Un bouton, pas de sondage automatique.
 */
export const refreshSelogerStatus = async (propertyId) => {
  const res = await axios.post(
    `${API}/properties/${propertyId}/diffusion/seloger/refresh-status`,
    {},
    { headers: authHeaders() }
  );
  return res.data;
};

/**
 * Lot AH6 — état réel de l'annonce chez LeBonCoin (filet de secours du webhook).
 * Le portail limite la fréquence de cette lecture (429) : un bouton, jamais de sondage.
 */
export const fetchLeboncoinMonitoring = async (propertyId) => {
  const res = await axios.post(
    `${API}/properties/${propertyId}/diffusion/leboncoin/monitoring`,
    {},
    { headers: authHeaders() }
  );
  return res.data;
};

/**
 * Bulk actions (LOT 3 bis / iter93).
 * @param {'activate'|'deactivate'} action
 * @param {string[]} propertyIds
 * @param {string[]} supports
 */
export const bulkDiffusion = async (action, propertyIds, supports) => {
  const res = await axios.post(
    `${API}/diffusion/bulk`,
    { action, property_ids: propertyIds, supports },
    { headers: authHeaders() }
  );
  return res.data;
};

/**
 * Flux CessionPME (lot AP) — génère le XML des biens diffusés et le dépose sur le FTP
 * de l'agence. Le portail vient lire le fichier : rien ne part par API.
 */
export const releverLeboncoinAgence = async () => {
  const res = await axios.post(`${API}/diffusion/leboncoin/relever`, {}, { headers: authHeaders() });
  return res.data;
};

export const envoyerFluxCessionPme = async () => {
  const res = await axios.post(`${API}/diffusion/cessionpme/envoyer`, {}, { headers: authHeaders() });
  return res.data;
};

/** Lot DN — les 20 derniers dépôts CessionPME de l'agence (succès comme échecs). */
export const listerDepotsCessionPme = async () => {
  const res = await axios.get(`${API}/diffusion/cessionpme/depots`, { headers: authHeaders() });
  return res.data?.depots || [];
};

/**
 * « Générer sans déposer » (lot AP) — construit l'archive et renvoie le compte rendu + le ZIP
 * en base64, SANS aucune connexion FTP et sans modifier le statut des biens.
 */
export const simulerFluxCessionPme = async (agenceId) => {
  const res = await axios.post(`${API}/diffusion/cessionpme/simuler`, { agence_id: agenceId },
                               { headers: authHeaders() });
  return res.data;
};
